import React, { useState } from 'react';
import { TypeBadge } from './TeamBrowser';
import { ITEMS_INFO, ABILITIES_INFO } from '../data/teams';
import { getSpriteUrl } from '../data/sprites';
import { getDefensiveProfile } from '../data/typeChart';

function StatBar({ label, value, max = 255 }) {
  const pct = (value / max) * 100;
  let color = '#e74c3c';
  if (value >= 120) color = '#2ecc71';
  else if (value >= 90) color = '#3498db';
  else if (value >= 60) color = '#f39c12';

  return (
    <div className="stat-bar-row">
      <span className="stat-label">{label}</span>
      <div className="stat-bar-bg">
        <div className="stat-bar-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="stat-value">{value}</span>
    </div>
  );
}

function EVDisplay({ evs }) {
  const labels = { hp: 'HP', atk: 'Atk', def: 'Def', spa: 'SpA', spd: 'SpD', spe: 'Spe' };
  const invested = Object.entries(evs).filter(([, v]) => v > 0);
  return (
    <div className="ev-display">
      {invested.map(([stat, val]) => (
        <span key={stat} className="ev-chip">
          {labels[stat]}: {Math.round(val / 8)} SP
        </span>
      ))}
    </div>
  );
}

function PokemonCard({ pokemon, expanded, onToggle }) {
  const defense = getDefensiveProfile(pokemon.types);

  return (
    <div className={`pokemon-card ${expanded ? 'expanded' : ''}`} onClick={onToggle}>
      <div className="pokemon-card-header">
        <div className="pokemon-identity">
          <div className="pokemon-identity-row">
            <img
              src={getSpriteUrl(pokemon.name)}
              alt={pokemon.name}
              className="pokemon-card-sprite"
              onError={e => { e.target.style.display = 'none'; }}
            />
            <h3>{pokemon.name}</h3>
          </div>
          <div className="pokemon-types">
            {pokemon.types.map(t => <TypeBadge key={t} type={t} />)}
          </div>
        </div>
        <div className="pokemon-role">{pokemon.role}</div>
      </div>

      {expanded && (
        <div className="pokemon-card-details">
          <div className="detail-grid">
            <div className="detail-section">
              <h4>Setup</h4>
              <div className="detail-row">
                <span className="detail-key">Ability:</span>
                <span className="detail-val ability-name" title={ABILITIES_INFO[pokemon.ability] || ''}>
                  {pokemon.ability}
                  {ABILITIES_INFO[pokemon.ability] && (
                    <span className="tooltip">{ABILITIES_INFO[pokemon.ability]}</span>
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-key">Item:</span>
                <span className="detail-val item-name" title={ITEMS_INFO[pokemon.item] || ''}>
                  {pokemon.item}
                  {ITEMS_INFO[pokemon.item] && (
                    <span className="tooltip">{ITEMS_INFO[pokemon.item]}</span>
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-key">Nature:</span>
                <span className="detail-val">{pokemon.nature}</span>
              </div>
              <div className="detail-row">
                <span className="detail-key">SP:</span>
                <EVDisplay evs={pokemon.evs} />
              </div>
              {pokemon.ivs && (
                <div className="detail-row">
                  <span className="detail-key">IVs:</span>
                  <span className="detail-val">
                    {Object.entries(pokemon.ivs).map(([k, v]) => `${v} ${k.toUpperCase()}`).join(', ')}
                  </span>
                </div>
              )}
            </div>

            <div className="detail-section">
              <h4>Moves</h4>
              <div className="moves-list">
                {pokemon.moves.map(m => (
                  <div key={m} className="move-item">{m}</div>
                ))}
              </div>
            </div>

            {pokemon.baseStats && (
              <div className="detail-section">
                <h4>{pokemon.megaStats ? 'Base Stats' : 'Base Stats'}</h4>
                <StatBar label="HP" value={pokemon.baseStats.hp} />
                <StatBar label="Atk" value={pokemon.baseStats.atk} />
                <StatBar label="Def" value={pokemon.baseStats.def} />
                <StatBar label="SpA" value={pokemon.baseStats.spa} />
                <StatBar label="SpD" value={pokemon.baseStats.spd} />
                <StatBar label="Spe" value={pokemon.baseStats.spe} />
                {pokemon.megaStats && (
                  <>
                    <h4 style={{marginTop: '1rem'}}>Mega Stats</h4>
                    <StatBar label="HP" value={pokemon.megaStats.hp} />
                    <StatBar label="Atk" value={pokemon.megaStats.atk} />
                    <StatBar label="Def" value={pokemon.megaStats.def} />
                    <StatBar label="SpA" value={pokemon.megaStats.spa} />
                    <StatBar label="SpD" value={pokemon.megaStats.spd} />
                    <StatBar label="Spe" value={pokemon.megaStats.spe} />
                  </>
                )}
              </div>
            )}

            <div className="detail-section">
              <h4>Defensive Profile</h4>
              {defense.immunities.length > 0 && (
                <div className="defense-row">
                  <span className="defense-label immune-label">Immune:</span>
                  <div className="defense-types">
                    {defense.immunities.map(t => <TypeBadge key={t} type={t} />)}
                  </div>
                </div>
              )}
              {defense.resistances.length > 0 && (
                <div className="defense-row">
                  <span className="defense-label resist-label">Resists:</span>
                  <div className="defense-types">
                    {defense.resistances.map(({ type, multiplier }) => (
                      <span key={type} className="defense-type-item">
                        <TypeBadge type={type} />
                        <span className="multiplier">x{multiplier}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {defense.weaknesses.length > 0 && (
                <div className="defense-row">
                  <span className="defense-label weak-label">Weak:</span>
                  <div className="defense-types">
                    {defense.weaknesses.map(({ type, multiplier }) => (
                      <span key={type} className="defense-type-item">
                        <TypeBadge type={type} />
                        <span className="multiplier">x{multiplier}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {pokemon.notes && (
            <div className="pokemon-notes">
              <strong>Notes:</strong> {pokemon.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamDetail({ team, onBack, onUseTeam }) {
  const [expandedPokemon, setExpandedPokemon] = useState(null);

  return (
    <div className="team-detail">
      <div className="detail-top-bar">
        <button className="back-btn" onClick={onBack}>&larr; Back to Teams</button>
        <button className="use-team-btn" onClick={() => onUseTeam(team)}>
          Use in Battle Advisor &rarr;
        </button>
      </div>

      <div className="team-detail-header">
        <h2>{team.name}</h2>
        <div className="team-meta">
          <span className="format-tag champions">
            {team.archetype}
          </span>
          <span className={`difficulty-tag ${team.difficulty.toLowerCase()}`}>
            {team.difficulty}
          </span>
          <span className="archetype-tag">{team.archetype}</span>
        </div>
      </div>

      <div className="strategy-section">
        <h3>Strategy</h3>
        <p>{team.strategy}</p>
      </div>

      <div className="leads-section">
        <h3>Lead Recommendations</h3>
        <div className="leads-grid">
          {team.leadRecommendations.map((rec, i) => (
            <div key={i} className="lead-card">
              <div className="lead-pokemon">
                {rec.pokemon.map(p => (
                  <span key={p} className="lead-name">{p}</span>
                ))}
              </div>
              <p className="lead-situation">{rec.situation}</p>
            </div>
          ))}
        </div>
        {team.backlineNotes && (
          <p className="backline-notes"><strong>Back-line:</strong> {team.backlineNotes}</p>
        )}
      </div>

      <div className="pokemon-section">
        <h3>The Team (click to expand)</h3>
        <div className="pokemon-list">
          {team.pokemon.map(p => (
            <PokemonCard
              key={p.name}
              pokemon={p}
              expanded={expandedPokemon === p.name}
              onToggle={() => setExpandedPokemon(expandedPokemon === p.name ? null : p.name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default TeamDetail;
